import { Chip, Stack, Tooltip } from '@mui/material';
import type { TriageIndex } from '../../api/useTriageIndex';

/**
 * The header half of the triage marks (spec §6.7). Without it a screen with no
 * chips reads as "nothing open" even when the map behind the chips could not
 * be read at all.
 *
 * 🚨 The state is keyed on the DATA (`index === undefined`), never on an error
 * flag: react-query's `isError` is sticky and keeps `data`, and this repo has
 * already lost an outage to reading that flag as truth. `useTriageIndex` does
 * not even expose it.
 */
export interface TriageIndexNoticeProps {
  /** `undefined` ⇒ the index NEVER loaded. */
  index: TriageIndex | undefined;
  /** Last success older than 3× the poll — the chips stay, greyed. */
  isStale: boolean;
  lastSuccessAt: number | null;
}

const chipSx = { fontSize: '0.65rem', height: 20 } as const;

const UNREADABLE_TITLE =
  'Triage marks UNREADABLE — the counter-stuck / orphaned-fill index could not be read. No row on this screen can be marked, so the absence of marks says NOTHING about whether a leg is waiting for a human.';

export function TriageIndexNotice({
  index,
  isStale,
  lastSuccessAt
}: TriageIndexNoticeProps): JSX.Element | null {
  if (!index) {
    return (
      <Tooltip title={UNREADABLE_TITLE}>
        <Chip
          size="small"
          label="⚠?"
          variant="outlined"
          data-triage-notice="unreadable"
          sx={{ ...chipSx, color: 'grey.500', borderColor: 'grey.700' }}
        />
      </Tooltip>
    );
  }

  const truncated = index.truncated === true;
  if (!truncated && !isStale) return null;

  const lastRead =
    lastSuccessAt != null
      ? new Date(lastSuccessAt).toLocaleTimeString()
      : 'unknown';

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {truncated && (
        <Tooltip title="The triage index hit its key cap — it is showing the first 2000 rules/pairs. Rows past the cap carry NO mark even when they have open cards.">
          <Chip
            size="small"
            label="⚠ triage truncated"
            color="warning"
            variant="outlined"
            data-triage-notice="truncated"
            sx={chipSx}
          />
        </Tooltip>
      )}
      {isStale && (
        <Tooltip title="The triage index has not refreshed recently — the marks below were true at the time shown, not now.">
          <Chip
            size="small"
            label={`⚠ triage stale — last read ${lastRead}`}
            variant="outlined"
            data-triage-notice="stale"
            sx={{ ...chipSx, color: 'grey.500', borderColor: 'grey.700' }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}
